import type {SidebarLink} from '@github-ui/saved-views'
import {ActionList, ActionMenu, IconButton} from '@primer/react'
import {CheckIcon, GearIcon, PencilIcon, RowsIcon, TrashIcon} from '@primer/octicons-react'
import {useAnalytics} from '@github-ui/use-analytics'
import type {RefObject} from 'react'

import {defaultViewConfig, type DefaultViewDashboardType} from '../../constants/default-view'
import {useInboxOptionsTipDismissed} from '../../hooks/use-inbox-options-tip-dismissed'
import {useSaveDefaultView} from '../../hooks/use-save-default-view'
import {InboxOptionsTip} from './InboxOptionsTip'

import styles from './OptionsButton.module.css'

export interface OptionsButtonProps {
  dashboardType: DefaultViewDashboardType
  currentViewId?: string
  defaultViewId?: string
  selectedLink: SidebarLink | undefined
  showSavedViewActions: boolean
  onEditView?: (shortcut: SidebarLink) => void
  onDeleteView?: (shortcut: SidebarLink) => void
  onUpdateView?: (shortcut: SidebarLink) => void
  isViewModified?: boolean
  onCustomizeSections?: () => void
  customizeSectionsButtonRef?: RefObject<HTMLButtonElement | null>
  showInboxOptionsTip?: boolean
  inboxOptionsTipDismissed?: boolean
  showDefaultViewOption?: boolean
}

export function OptionsButton({
  dashboardType,
  currentViewId,
  defaultViewId,
  selectedLink,
  showSavedViewActions,
  onEditView,
  onDeleteView,
  onUpdateView,
  isViewModified,
  onCustomizeSections,
  customizeSectionsButtonRef,
  showInboxOptionsTip = false,
  inboxOptionsTipDismissed = false,
  showDefaultViewOption = true,
}: OptionsButtonProps) {
  const {analyticsCategory, destinationPath, fallbackViewId} = defaultViewConfig[dashboardType]
  const {isSaving, isDefault, saveDefaultView, clearDefaultView} = useSaveDefaultView(
    dashboardType,
    currentViewId,
    defaultViewId,
  )
  const {isDismissed, dismiss} = useInboxOptionsTipDismissed(inboxOptionsTipDismissed)
  const {sendAnalyticsEvent} = useAnalytics()

  const savedViewActions =
    showSavedViewActions && selectedLink && onEditView && onDeleteView && onUpdateView
      ? {selectedLink, onEditView, onDeleteView, onUpdateView}
      : undefined

  const handleToggleDefaultView = () => {
    if (isDefault) {
      sendAnalyticsEvent(analyticsCategory, 'CLEAR_DEFAULT_VIEW', {view: currentViewId ?? fallbackViewId})
      clearDefaultView()
    } else {
      sendAnalyticsEvent(analyticsCategory, 'SET_DEFAULT_VIEW', {view: currentViewId ?? fallbackViewId})
      saveDefaultView()
    }
  }
  const handleOpenChange = (open: boolean) => {
    if (open && showInboxOptionsTip && !isDismissed) {
      dismiss()
    }
  }

  const ariaLabel = savedViewActions && isViewModified ? 'View options (update available)' : 'View options'

  return (
    <div className={styles.optionsButtonContainer}>
      <ActionMenu anchorRef={customizeSectionsButtonRef} onOpenChange={handleOpenChange}>
        <ActionMenu.Anchor>
          <IconButton
            ref={customizeSectionsButtonRef}
            icon={GearIcon}
            aria-label={ariaLabel}
            className={savedViewActions && isViewModified ? styles.notificationIndicator : undefined}
          />
        </ActionMenu.Anchor>
        <ActionMenu.Overlay width="small">
          <ActionList>
            {showDefaultViewOption && (
              <ActionList.Group selectionVariant="multiple">
                <ActionList.Item disabled={isSaving} selected={isDefault} onSelect={handleToggleDefaultView}>
                  Set as default view
                  <ActionList.Description variant="block">
                    Default to this view when visiting {destinationPath}
                  </ActionList.Description>
                </ActionList.Item>
              </ActionList.Group>
            )}
            {onCustomizeSections && (
              <>
                <ActionList.Divider />
                <ActionList.Item onSelect={onCustomizeSections}>
                  <ActionList.LeadingVisual>
                    <RowsIcon />
                  </ActionList.LeadingVisual>
                  Customize sections
                </ActionList.Item>
              </>
            )}
            {savedViewActions && (
              <>
                <ActionList.Divider />
                {isViewModified && (
                  <ActionList.Item onSelect={() => savedViewActions.onUpdateView(savedViewActions.selectedLink)}>
                    <ActionList.LeadingVisual>
                      <CheckIcon />
                    </ActionList.LeadingVisual>
                    Update view with current query
                  </ActionList.Item>
                )}
                <ActionList.Item onSelect={() => savedViewActions.onEditView(savedViewActions.selectedLink)}>
                  <ActionList.LeadingVisual>
                    <PencilIcon />
                  </ActionList.LeadingVisual>
                  Edit view
                </ActionList.Item>
                <ActionList.Divider />
                <ActionList.Item
                  variant="danger"
                  onSelect={() => savedViewActions.onDeleteView(savedViewActions.selectedLink)}
                >
                  <ActionList.LeadingVisual>
                    <TrashIcon />
                  </ActionList.LeadingVisual>
                  Delete
                </ActionList.Item>
              </>
            )}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      {showInboxOptionsTip && !isDismissed && <InboxOptionsTip onDismiss={dismiss} />}
    </div>
  )
}
